import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const COMPANY_TYPES = [
  { value: "hotel", label: "Hôtel" },
  { value: "conciergerie", label: "Agence de conciergerie" },
  { value: "agence_voyage", label: "Agence de voyage" },
  { value: "corporate", label: "Corporate" },
  { value: "autre", label: "Autre" },
];

const ConnectApplyPage = () => {
  const [form, setForm] = useState({
    company_name: "", full_name: "", position: "", email: "", phone: "",
    website: "", company_type: "hotel", city: "", country: "France",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/connect/auth/apply`, form);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur lors de la soumission");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="apply-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Demande envoyée</h1>
          <p className="text-[#1A1A1A]/70 font-light mb-8">
            Votre demande d'accès à Altura Connect a été soumise. Notre équipe examinera votre dossier et vous contactera dans les meilleurs délais.
          </p>
          <Link to="/connect" className="btn-dark inline-flex items-center gap-3">
            Retour <ArrowRight size={16} weight="bold" />
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#EDEBE4] py-20 px-6" data-testid="connect-apply-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto">
        <div className="text-center mb-10">
          <Link to="/" className="inline-block mb-6">
            <span className="font-heading text-2xl tracking-[0.35em] text-[#1A1A1A] uppercase">Altura</span>
          </Link>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-2">Devenir partenaire</h1>
          <p className="text-[#1A1A1A]/60 text-sm font-light">Rejoignez le réseau Altura Connect</p>
        </div>

        <div className="bg-white border border-[#1A1A1A]/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nom de l'entreprise *</label>
              <input type="text" name="company_name" value={form.company_name} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-company" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nom complet *</label>
                <input type="text" name="full_name" value={form.full_name} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-name" />
              </div>
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Poste *</label>
                <input type="text" name="position" value={form.position} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-position" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-email" />
              </div>
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Téléphone *</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-phone" />
              </div>
            </div>
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Site internet</label>
              <input type="url" name="website" value={form.website} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder="https://" data-testid="apply-website" />
            </div>
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Type d'entreprise *</label>
              <select name="company_type" value={form.company_type} onChange={handleChange} className="input-luxury w-full bg-transparent" data-testid="apply-type">
                {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Ville *</label>
                <input type="text" name="city" value={form.city} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-city" />
              </div>
              <div>
                <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Pays *</label>
                <input type="text" name="country" value={form.country} onChange={handleChange} required className="input-luxury w-full bg-transparent" data-testid="apply-country" />
              </div>
            </div>

            {error && <p className="text-red-600 text-sm" data-testid="apply-error">{error}</p>}

            <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3" data-testid="apply-submit-btn">
              {loading ? "Envoi..." : "Soumettre ma demande"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/connect" className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors">
            Déjà partenaire ? Se connecter
          </Link>
        </div>
      </motion.div>
    </main>
  );
};

export default ConnectApplyPage;
